import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { East, InfoOutlined } from "@mui/icons-material";
import { completeOrderWithoutPayment } from "../../../api/order-summary.api";
import "./PriceDetails.scss";

interface PriceDetailsProps {
  orderSummary: any;
  partnerId: number;
  billingValid: boolean;
  isFromCartPage: boolean;
  profileDetails: any;
  serviceDate?: string;
  serviceDateType?: string;
  isServiceDateValid?: boolean;
  isFromPlans?: boolean;
  plannerId?: number;
  billingDetails?: any;
}

const PriceDetails: React.FC<PriceDetailsProps> = ({
  orderSummary,
  partnerId,
  billingValid,
  isFromCartPage,
  profileDetails,
  serviceDate,
  serviceDateType,
  isServiceDateValid = true,
  isFromPlans,
  plannerId,
  billingDetails,
}) => {
  const [showProfileModal, setShowProfileModal] = useState(false);

  const navigate = useNavigate();
  const itemCount = orderSummary.totalItems;
  const subtotal = orderSummary.subTotal || 0;
  const discount = orderSummary.discountAmount;
  const taxFee = orderSummary.taxFee;
  const serviceFee = orderSummary.serviceFee;
  const partners = orderSummary?.partnerList || [];
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const preNeedTotal = Number(orderSummary?.PreneedTotal) || Number(orderSummary?.preNeedTotal) || 0;
  const immediateNeedTotal = Number(orderSummary?.ImmediateNeedTotal) || Number(orderSummary?.immediateNeedTotal) || 0;
  const total = subtotal - discount + taxFee + serviceFee;

  const isPlanner = isFromPlans || (partners.length > 0 && partners.every((p: any) => !!p.plannerId || Array.isArray(p?.packages)));

  const renderNeedType = (needType: string) => {
    if (!needType) return null;
    const isPreNeed = needType === "pre_need";
    const label = isPreNeed ? "Preneed" : "Immediate Need";
    return (
      <>
        <Box
          component="span"
          sx={{
            color: "#084145",
            mx: 1,
            fontWeight: 900,
            fontSize: "18px",
            verticalAlign: "middle",
          }}
        >
          &bull;
        </Box>
        <Box
          component="span"
          sx={{
            color: "#000",
            fontSize: "14px",
            fontWeight: 400,
            verticalAlign: "middle",
          }}
        >
          {label}
        </Box>
      </>
    );
  };


  const isProfileComplete = Boolean(
    profileDetails?.first_name &&
    profileDetails?.last_name &&
    profileDetails?.email &&
    profileDetails?.mobileNumber
  );

  const isPaymentDisabled =
    !billingValid || (serviceDateType === "specify" && !isServiceDateValid);

  const handlePaymentClick = async () => {
    if (isPaymentDisabled) return;

    if (!isProfileComplete) {
      setShowProfileModal(true);
      return;
    }

    if (!isPlanner && immediateNeedTotal === 0) {
      try {
        const response = await completeOrderWithoutPayment(orderSummary.orderId);
        if (response?.data?.success) {
          navigate("/payment-status", {
            state: {
              paymentStatus: true,
              isOrderSuccess: true,
              details: {
                orderId: orderSummary.orderId,
                grandTotal: 0,
                txnId: `ORD-${Date.now()}`,
                transactionDate: new Date(),
              },
            },
          });
        }
      } catch (error) {
        console.error("Error completing order:", error);
      }
      return;
    }

    navigate("payment", {
      state: {
        orderSummary: orderSummary,
        partnerId: partnerId,
        isFromCartPage: isFromCartPage,
        serviceDate: serviceDateType === "specify" ? serviceDate : null,
        isFromPlans: isFromPlans,
        plannerId: plannerId,
        immediateNeedTotal: immediateNeedTotal,
      },
    });
  };


  return (
    <Box className="order-summary">
      <Typography className="order-summary__section-title">
        Price Details
      </Typography>

      <Box className="order-summary__box order-summary__box--top">
        <Box className="order-summary__row">
          <Typography className="order-summary__label">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </Typography>
          <Typography className="order-summary__value">
            $ {formatCurrency(subtotal ?? 0)}
          </Typography>
        </Box>
      </Box>

      {serviceDateType === "specify" && (
        <Box className="order-summary__box order-summary__box--date">
          <Box className="order-summary__row">
            <Typography className="order-summary__label">
              Service Date
            </Typography>
            <Typography className="order-summary__value">
              {serviceDate
                ? new Date(serviceDate).toLocaleDateString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })
                : "-"}
            </Typography>
          </Box>
        </Box>
      )}

      <Box className="order-summary__box order-summary__box--details">
        {partners?.map((partnerObj: any, index: number) => {
          if (
            Array.isArray(partnerObj?.services) &&
            partnerObj.services.length > 0
          ) {
            return partnerObj.services.map(
              (service: any, serviceIndex: number) => (
                <Box
                  className="order-summary__row"
                  key={`${index}-service-${serviceIndex}`}
                >
                  <Typography className="order-summary__label">
                    {service?.serviceName || "-"}
                    {!isPlanner && renderNeedType(service?.needType)}
                  </Typography>
                  <Typography className="order-summary__value">
                    $ {formatCurrency(service?.serviceTotalPrice ?? service?.price ?? 0)}
                  </Typography>
                </Box>
              )
            );
          }

          if (
            Array.isArray(partnerObj?.packages) &&
            partnerObj.packages.length > 0
          ) {
            return partnerObj.packages.map((pkg: any, packageIndex: number) => (
              <Box key={`${index}-package-${packageIndex}`}>
                <Box className="order-summary__row" sx={{ marginBottom: "0px !important" }}>
                  <Typography className="order-summary__label">
                    {pkg?.packageName && (
                      <Typography sx={{ fontWeight: "600 !important", fontSize: "14px", color: "#000" }}>
                        {pkg.packageName}
                      </Typography>
                    )}
                  </Typography>
                  <Typography className="order-summary__value">
                    $ {formatCurrency(pkg?.packageTotalPrice ?? pkg?.price ?? partnerObj?.partnerTotalPrice ?? 0)}
                  </Typography>
                </Box>

              </Box>
            ));
          }

          let partnerName = "";
          let partnerPrice = 0;

          if (partnerObj && typeof partnerObj === "object") {
            if (partnerObj.plannerName) {
              partnerName = partnerObj.plannerName;
              partnerPrice = partnerObj.partnerTotalPrice ?? partnerObj.price ?? 0;
            } else if (partnerObj.partnerName) {
              partnerName = partnerObj.partnerName;
              partnerPrice = partnerObj.partnerTotalPrice ?? partnerObj.price ?? 0;
            } else {
              const keys = Object.keys(partnerObj);
              if (keys.length > 0) {
                partnerName = keys[0];
                partnerPrice = partnerObj[partnerName];
              }
            }
          }

          return (
            <Box className="order-summary__row" key={index}>
              <Typography className="order-summary__label">
                {partnerName || "-"}
                {!isPlanner && renderNeedType(
                  partnerObj?.partnerPreNeedTypesTotalPrice > 0
                    ? "pre_need"
                    : "at_need"
                )}
              </Typography>
              <Typography className="order-summary__value">
                $ {formatCurrency(partnerPrice)}
              </Typography>
            </Box>
          );
        })}

        <Box className="order-summary__row">
          <Typography className="order-summary__label">Taxes</Typography>
          <Typography className="order-summary__value">
            $ {formatCurrency(taxFee)}
          </Typography>
        </Box>

        <Divider className="order-summary__divider" />

        <Box className="order-summary__row order-summary__row--total">
          <Typography className="order-summary__label">Total*</Typography>
          <Typography className="order-summary__value">
            $ {formatCurrency(total)}
          </Typography>
        </Box>

        {!isPlanner && (
          <>
            <Divider className="order-summary__divider" />

            <Box className="order-summary__row">
              <Typography className="order-summary__label">Preneed Total</Typography>
              <Typography className="order-summary__value">
                $ {formatCurrency(preNeedTotal)}
              </Typography>
            </Box>

            <Box className="order-summary__row">
              <Typography className="order-summary__label">
                Immediate Need Total
              </Typography>
              <Typography className="order-summary__value">
                $ {formatCurrency(immediateNeedTotal)}
              </Typography>
            </Box>
          </>
        )}

        <Typography className="order-summary__billing-info">
          *Billing applies only to Immediate Need Services and Preneed Planner on MemorialGo. Preneed Service payments will be finalized with the Provider.
        </Typography>

      </Box>

      <Button
        fullWidth
        className={`order-summary__btn ${!isPaymentDisabled ? "enabled" : "disabled"
          }`}
        onClick={handlePaymentClick}
        endIcon={<East />}
        disabled={isPaymentDisabled}
      >
        {isPlanner || immediateNeedTotal > 0 ? "Proceed to Payment" : "Complete Order"}
      </Button>


      {!billingValid && (
        <Box className="order-summary__info-msg">
          <InfoOutlined />
          <Typography>Add billing details to continue</Typography>
        </Box>
      )}

      {billingValid &&
        serviceDateType === "specify" &&
        !isServiceDateValid && (
          <Box className="order-summary__info-msg">
            <InfoOutlined />
            <Typography>Please select a future date and time</Typography>
          </Box>
        )}

      <Dialog
        open={showProfileModal}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setShowProfileModal(false);
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "center",
            pb: 0,
          }}
          className="book-consultant-popup__title"
        >
          ⚠️ Profile Incomplete
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            First Name, Last Name, Email Address, and Phone Number are required
            to proceed with payment
          </Typography>

          <Typography sx={{ mt: 3 }}>
            Please complete your profile details to continue
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 1, py: 2, display: "flex", gap: 2 }}>
          <Button
            onClick={() => setShowProfileModal(false)}
            variant="outlined"
            className="share-partner-popup__btn share-partner-popup__btn--copy"
          >
            Cancel
          </Button>

          <Button
            onClick={() =>
              navigate("/user-profile", {
                state: { billingDetails, fromOrderSummary: true },
              })
            }
            className="share-partner-popup__btn share-partner-popup__btn--share"
          >
            Complete Profile
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PriceDetails;